import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { ONE_WEEK_IN_MS } from '@/src/general/constants/time'
import { storage } from '@/src/general/utilities/storage'
import { STORAGE_KEYS } from '@/src/general/utilities/storage/MMKVStorage'

import { prayApi } from '../../pray/api/prayApi'
import { PRAY_OBJECT_TYPE } from '../../pray/models/prayObjectType'
import { INTENTION_TYPE } from '../models/Intention.enums'
import type { TTypeIntention } from '../models/Intention.types'
import { getIntentionQueryOptions } from './useIntentionQuery'

type TPrayedIntention = {
  prayedIntentions: Record<number, number>
  AddNewIntentionInThePrayedList: (id: number) => void
  removeIntentionsOlderThanAWeek: VoidFunction
}

export const useIntentionStore = create(
  persist<TPrayedIntention>(
    (set, get) => ({
      prayedIntentions: {},
      AddNewIntentionInThePrayedList: (id: number) => {
        return set({ prayedIntentions: { ...get().prayedIntentions, [id]: new Date().valueOf() } })
      },
      removeIntentionsOlderThanAWeek: () =>
        set(({ prayedIntentions, ...state }) => {
          const filteredPrayedIntentions = Object.entries(prayedIntentions).reduce<Record<number, number>>(
            (acc, [id, timestamp]) => {
              if (timestamp >= Date.now() - ONE_WEEK_IN_MS) {
                acc[Number(id)] = timestamp
              }
              return acc
            },
            {},
          )
          return { ...state, prayedIntentions: filteredPrayedIntentions }
        }),
    }),
    { name: STORAGE_KEYS.PRAYED_INTENTIONS, storage: createJSONStorage(() => storage) },
  ),
)

type TMutationOptions = Pick<TTypeIntention, 'id' | 'type'> & { landingPageSlug: string | undefined }

export const useIntentionPrayMutation = () => {
  const queryClient = useQueryClient()
  const { AddNewIntentionInThePrayedList } = useIntentionStore((state) => state)

  return useMutation({
    mutationFn: ({ id, type, landingPageSlug }: TMutationOptions) =>
      prayApi.createPrayer({ objectType: matchingType[type], objectId: id, landingPageSlug }),
    onSuccess: (_, options) => {
      AddNewIntentionInThePrayedList(options.id)
      optimisticUpdateForHasPrayedProperty(queryClient, options)
    },
  })
}

const optimisticUpdateForHasPrayedProperty = (queryClient: QueryClient, { id, type }: TMutationOptions) =>
  queryClient.setQueryData(
    getIntentionQueryOptions(id, undefined, type).queryKey,
    (oldData: TTypeIntention | undefined) =>
      oldData && {
        ...oldData,
        hasPrayed: true,
        prayersCount: oldData.prayersCount + 1,
      },
  )

const matchingType = {
  [INTENTION_TYPE.INTENTION]: PRAY_OBJECT_TYPE.INTENTION,
  [INTENTION_TYPE.ARRIVAL]: PRAY_OBJECT_TYPE.ARRIVAL,
  [INTENTION_TYPE.BIRTHDAY]: PRAY_OBJECT_TYPE.BIRTHDAY,
} as const
